import { useEffect, useState } from "react";
import {
  X,
  Activity,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Zap,
  Webhook,
  Pencil,
  RefreshCcw,
  FileText,
  Mail,
  MailX,
  MailWarning,
} from "lucide-react";

import { api } from "@/lib/api";

const ICON_FOR_EVENT = {
  webhook_received: Webhook,
  record_created: FileText,
  serial_added: Zap,
  serial_updated: Pencil,
  manual_edit: Pencil,
  status_changed: ChevronRight,
  mint_attempt_started: Loader2,
  mint_tx_submitted: Zap,
  mint_success: CheckCircle2,
  mint_failed: AlertTriangle,
  requeued: RefreshCcw,
  email_sent: Mail,
  email_failed: MailWarning,
  email_skipped: MailX,
};

const COLOR_FOR_EVENT = {
  mint_success: "text-green-700 bg-green-50 border-green-200",
  mint_failed: "text-red-700 bg-red-50 border-red-200",
  mint_tx_submitted: "text-indigo-700 bg-indigo-50 border-indigo-200",
  mint_attempt_started: "text-indigo-700 bg-indigo-50 border-indigo-200",
  serial_added: "text-amber-800 bg-amber-50 border-amber-200",
  serial_updated: "text-amber-800 bg-amber-50 border-amber-200",
  manual_edit: "text-gray-700 bg-gray-50 border-gray-200",
  requeued: "text-violet-800 bg-violet-50 border-violet-200",
  status_changed: "text-blue-800 bg-blue-50 border-blue-200",
  webhook_received: "text-purple-800 bg-purple-50 border-purple-200",
  record_created: "text-gray-800 bg-gray-50 border-gray-200",
  email_sent: "text-emerald-800 bg-emerald-50 border-emerald-200",
  email_failed: "text-red-700 bg-red-50 border-red-200",
  email_skipped: "text-gray-600 bg-gray-50 border-gray-200",
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

function LogRow({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = ICON_FOR_EVENT[entry.event_type] || Activity;
  const color = COLOR_FOR_EVENT[entry.event_type] || "text-gray-700 bg-gray-50 border-gray-200";
  const hasMeta = entry.metadata && Object.keys(entry.metadata).length > 0;
  return (
    <div
      data-testid={`activity-row-${entry.event_type}`}
      className="flex gap-3 px-5 py-3 border-b border-gray-100 last:border-0"
    >
      <div
        className={`shrink-0 h-8 w-8 rounded-lg border flex items-center justify-center ${color}`}
      >
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 justify-between">
          <div className="text-sm font-medium text-gray-900 truncate">
            {entry.message}
          </div>
          <div className="text-[11px] font-mono text-gray-400 shrink-0">
            {fmtTime(entry.at)}
          </div>
        </div>
        <div className="mt-0.5 text-[11px] tracking-wider uppercase text-gray-500">
          {entry.event_type}
        </div>
        {hasMeta && (
          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid="activity-row-meta-toggle"
            className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-[#0055FF]"
          >
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            metadata
          </button>
        )}
        {expanded && hasMeta && (
          <pre className="mt-2 text-[11px] font-mono bg-gray-50 border border-gray-200 rounded-md p-2 overflow-x-auto text-gray-700">
            {JSON.stringify(entry.metadata, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

export default function ActivityLogDialog({ open, receipt, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !receipt?.id) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get(`/receipts/${receipt.id}/activity`)
      .then(({ data }) => {
        if (!cancelled) setEntries(data.entries || []);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e?.response?.data?.detail || "Failed to load activity");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, receipt?.id]);

  if (!open || !receipt) return null;

  return (
    <div
      data-testid="activity-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-[2px] overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden my-8"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-lg bg-blue-50 text-[#0055FF] flex items-center justify-center">
              <Activity size={16} />
            </span>
            <div>
              <div className="font-display font-semibold text-gray-900">
                Activity Log
              </div>
              <div className="text-[11px] tracking-wider uppercase text-gray-500 font-mono">
                {receipt.receipt_id}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="activity-dialog-close"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-6 py-12 text-center text-gray-500">Loading…</div>
          )}
          {!loading && error && (
            <div className="px-6 py-8 text-center text-red-700">{error}</div>
          )}
          {!loading && !error && entries.length === 0 && (
            <div
              data-testid="activity-empty"
              className="px-6 py-14 text-center"
            >
              <div className="mx-auto h-10 w-10 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center">
                <Activity size={16} />
              </div>
              <div className="mt-3 text-sm font-medium text-gray-900">
                No activity yet
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Events will appear here as the record moves through its lifecycle.
              </div>
            </div>
          )}
          {!loading && !error && entries.length > 0 && (
            <div data-testid="activity-list">
              {entries.map((entry, i) => (
                <LogRow key={i} entry={entry} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
